import { useAssetsStore, useBaseInfoStore, useExchangeInfoStore, useQuoteStore } from 'store'
import { UiUtils } from '../ui-utils/ui-utils'
import { useI18n } from 'i18n-utils'
import { ApiStatus, ContractOrderType, ContractServices, OrderDirection, ReduceOnly, TriggerType, type OrderBuyParams } from 'network'

export const useStopProfitStopLoss = (position: ApiResponse.DoubleContractPosition) => {
  const stopProfitStopLossVisible = ref(false)
  const exchangeStore = useExchangeInfoStore()
  const baseInfoStore = useBaseInfoStore()
  const quoteStore = useQuoteStore()
  const sliderValue = ref(0)
  const amount = ref('0')
  const sliderProfitRateValue = ref(0)
  const sliderLossRateValue = ref(0)
  const assetsStore = useAssetsStore()

  // 止盈相关
  const profitPrice = ref('')
  const percentProfit = ref('')
  const profit = ref('')
  const profitRate = ref('')

  // 止损相关
  const lossPrice = ref('')
  const percentLoss = ref('')
  const loss = ref('')
  const lossRate = ref('')

  const allPosition = ref(true)
  const isMarketPrice = ref(true)
  const lossIsMarketPrice = ref(true)

  const submitLoading = ref(false)
  const { t } = useI18n()

  const open = () => {
    stopProfitStopLossVisible.value = true
  }
  const close = () => {
    stopProfitStopLossVisible.value = false
  }

  const getSymbolInfo = () => {
    return exchangeStore.getContractSymbolInfoBySymbolId(position.symbolId)
  }

  const getSymbolIcon = () => {
    const symbolInfo = getSymbolInfo()
    if (!symbolInfo?.baseItemId) return ''
    const config = baseInfoStore.getCoinInfoByItemId(symbolInfo!.baseItemId)
    return config?.itemDetail.coinUrl
  }

  const markPrice = computed(() => {
    const quote = quoteStore.getSymbolBySymbolIdWithContractList(position.symbolId)
    return quote?.markPrice
  })

  const lastPrice = computed(() => {
    const quote = quoteStore.getSymbolBySymbolIdWithContractList(position.symbolId)
    return quote?.last
  })

  const symbolInfo = computed(() => getSymbolInfo())

  const switchAllPosition = () => {
    allPosition.value = !allPosition.value
    if (!allPosition.value) {
      sliderValue.value = 100
    }
  }

  const openInterest = computed(() => {
    return position.qty.multiplyStr(symbolInfo.value?.contractValue || 1).removeTrailingZero
  })

  const totalAmount = computed(() => {
    return openInterest.value.multiplyStr(markPrice.value || 1)
  })

  onBeforeMount(() => {
    amount.value = openInterest.value.multiplyStr(markPrice.value || 1)
  })

  const sliderMarks = {
    0: '0%',
    25: '25%',
    50: '50%',
    75: '75%',
    100: '100%',
  }

  const onAmountInput = (value: string) => {
    value = value || '0'
    sliderValue.value = value.divideStr(totalAmount.value).multiplyStr(100).cutDecimal(0).toNumber()
    calculateProfitLoss({ isStopProfit: true, price: profitPrice.value, percent: '', profit: '', rate: '' })
    calculateProfitLoss({ isStopProfit: false, price: lossPrice.value, percent: '', profit: '', rate: '' })
  }

  const closeAmount = computed(() => {
    if (allPosition.value) return openInterest.value
    return amount.value.divideStr(totalAmount.value).multiplyStr(openInterest.value)
  })

  const onSliderInput = (value: number) => {
    const config = exchangeStore.getContractSymbolInfoBySymbolId(position.symbolId)
    amount.value = value
      .multiplyStr(totalAmount.value)
      .divideStr(100)
      .cutDecimal(config?.quantityPrecision || 2).removeTrailingZero

    calculateProfitLoss({ isStopProfit: true, price: profitPrice.value, percent: '', profit: '', rate: '' })
    calculateProfitLoss({ isStopProfit: false, price: lossPrice.value, percent: '', profit: '', rate: '' })
  }

  const switchMarketPrice = () => {
    isMarketPrice.value = !isMarketPrice.value
  }

  const calculatePercent = (price: string) => {
    return price.subtractStr(position.holdPrice).divideStr(position.holdPrice).multiplyStr(100).adjustDecimal(2).toStringAsFixed(2)
  }

  const calculateProfit = (price: string) => {
    const _amount = closeAmount.value

    const buySide = position.direction === 1 ? 1 : -1
    return price.subtractStr(position.holdPrice).multiplyStr(_amount).multiplyStr(buySide).adjustDecimal(2).toStringAsFixed(2)
  }

  const calculateRate = (profit: string, isStopProfit: boolean) => {
    const _amount = closeAmount.value
    const rate = profit.divideStr(position.holdPrice.multiplyStr(_amount).divideStr(position.leverage)).multiplyStr(100).adjustDecimal(2).toStringAsFixed(2)

    if (isStopProfit) {
      sliderProfitRateValue.value = rate.toNumber()
      if (sliderProfitRateValue.value.isGreaterThanOrEqualTo(200)) {
        sliderProfitRateValue.value = 200
      } else if (sliderProfitRateValue.value.isLessThanOrEqualTo(0)) {
        sliderProfitRateValue.value = 0
      }
    } else {
      if (rate.isLessThanOrEqualTo(-200)) {
        sliderLossRateValue.value = 200
      } else if (rate.isGreaterThanOrEqualTo(0)) {
        sliderLossRateValue.value = 0
      } else {
        sliderLossRateValue.value = -rate.toNumber()
      }
    }

    return rate
  }

  const calculateProfitLoss = (data: { isStopProfit: boolean; price: string; percent: string; profit: string; rate: string }) => {
    const config = exchangeStore.getContractSymbolInfoBySymbolId(position.symbolId)
    const { price, percent, profit: _profit, rate } = data
    const isStopProfit = data.isStopProfit
    const buySide = position.direction === 1 ? 1 : -1

    const priceController = isStopProfit ? profitPrice : lossPrice
    const percentController = isStopProfit ? percentProfit : percentLoss
    const profitLossController = isStopProfit ? profit : loss
    const profitLossRateController = isStopProfit ? profitRate : lossRate

    const _amount = closeAmount.value

    const openPrice = position.holdPrice

    // 价格主导
    if (price) {
      percentController.value = calculatePercent(price)
      profitLossController.value = calculateProfit(price)
      profitLossRateController.value = calculateRate(profitLossController.value, isStopProfit)
    } else if (percent) {
      priceController.value = percent
        .divideStr(100)
        .multiplyStr(openPrice)
        .addStr(openPrice)
        .toStringAsFixed(config?.pricePrecision || 2)

      if (priceController.value.isLessThanOrEqualTo(0)) {
        priceController.value = '0'
      }
      profitLossController.value = calculateProfit(priceController.value)
      profitLossRateController.value = calculateRate(profitLossController.value, isStopProfit)
    } else if (_profit) {
      priceController.value = _profit
        .multiplyStr(buySide)
        .divideStr(_amount)
        .addStr(openPrice)
        .toStringAsFixed(config?.pricePrecision || 2)
      if (priceController.value.isLessThanOrEqualTo(0)) {
        priceController.value = '0'
      }
      percentController.value = calculatePercent(priceController.value)
      profitLossRateController.value = calculateRate(_profit, isStopProfit)
    } else if (rate) {
      profitLossController.value = rate.divideStr(100).multiplyStr(_amount).multiplyStr(openPrice).divideStr(position.leverage).adjustDecimal(2).toStringAsFixed(2)
      priceController.value = profitLossController.value
        .multiplyStr(buySide)
        .divideStr(_amount)
        .addStr(openPrice)
        .toStringAsFixed(config?.pricePrecision || 2)

      if (priceController.value.isLessThanOrEqualTo(0)) {
        priceController.value = '0'
      }

      percentController.value = calculatePercent(priceController.value)
    }
  }

  const onProfitRateSliderInput = (value: number) => {
    profitRate.value = value.toString()
    sliderProfitRateValue.value = value
    calculateProfitLoss({ isStopProfit: true, price: '', percent: '', profit: '', rate: value.toString() })
  }

  const onLossRateSliderInput = (value: number) => {
    lossRate.value = value.toString().multiplyStr(-1)
    sliderLossRateValue.value = value
    calculateProfitLoss({ isStopProfit: false, price: '', percent: '', profit: '', rate: lossRate.value })
  }

  const onProfitRateInput = (value: string) => {
    profitRate.value = value
    if (value.isGreaterThanOrEqualTo(200)) {
      sliderProfitRateValue.value = 200
    } else if (value.isLessThanOrEqualTo(0)) {
      sliderProfitRateValue.value = 0
    } else {
      sliderProfitRateValue.value = +value
    }
    calculateProfitLoss({ isStopProfit: true, price: '', percent: '', profit: '', rate: value })
    if (!value.trim()) {
      profitPrice.value = ''
    }
  }

  const onLossRateInput = (value: string) => {
    lossRate.value = value
    if (value.isLessThanOrEqualTo(-200)) {
      sliderLossRateValue.value = 200
    } else if (value.isGreaterThanOrEqualTo(0)) {
      sliderLossRateValue.value = 0
    } else {
      sliderLossRateValue.value = -value
    }
    calculateProfitLoss({ isStopProfit: false, price: '', percent: '', profit: '', rate: value })
    if (!value.trim()) {
      lossPrice.value = ''
    }
  }

  const onProfitPriceInput = (value: string) => {
    calculateProfitLoss({ isStopProfit: true, price: value, percent: '', profit: '', rate: '' })
  }

  const onLossPriceInput = (value: string) => {
    calculateProfitLoss({ isStopProfit: false, price: value, percent: '', profit: '', rate: '' })
  }

  const switchLossIsMarketPrice = () => {
    lossIsMarketPrice.value = !lossIsMarketPrice.value
  }

  const profitRateSliderMarks = {
    0: '0%',
    50: '50%',
    100: '100%',
    150: '150%',
    200: '200%',
  }

  const lossRateSliderMarks = {
    0: '0%',
    50: '-50%',
    100: '-100%',
    150: '-150%',
    200: '-200%',
  }

  /**
   * 确认止盈止损
   */
  const onConfirmStopProfitStopLoss = async () => {
    const number = amount.value || '0'
    if (!allPosition.value && number.isLessThanOrEqualTo(0)) throw UiUtils.showError(t('请输入数量'))
    const _profitPrice = profitPrice.value || '0'
    const _lossPrice = lossPrice.value || '0'
    if (_profitPrice.isLessThanOrEqualTo(0) && _lossPrice.isLessThanOrEqualTo(0)) throw UiUtils.showError(t('请输入止盈触发价或者止损触发价'))
    const profitPriceType = isMarketPrice.value ? t('标记价格') : t('最新价格')
    const lossPriceType = lossIsMarketPrice.value ? t('标记价格') : t('最新价格')

    const positionType = position.positionType

    const profitPriceCompare = isMarketPrice.value ? markPrice.value : lastPrice.value
    const lossPriceCompare = lossIsMarketPrice.value ? markPrice.value : lastPrice.value

    if (positionType === OrderDirection.Buy) {
      // 做多
      if (_profitPrice.isGreaterThan(0) && _profitPrice.isLessThanOrEqualTo(profitPriceCompare!)) {
        throw UiUtils.showError(t('做多时止盈触发价需大于{type}', { type: profitPriceType }))
      }
      if (_lossPrice.isLessThan(0) && _lossPrice.isGreaterThanOrEqualTo(lossPriceCompare!)) {
        throw UiUtils.showError(t('做多时止损触发价需小于{type}', { type: lossPriceType }))
      }
    }
    if (positionType === OrderDirection.Sell) {
      // 做空
      if (_profitPrice.isGreaterThan(0) && _profitPrice.isGreaterThanOrEqualTo(profitPriceCompare!)) {
        throw UiUtils.showError(t('做空时止盈触发价需小于{type}', { type: profitPriceType }))
      }
      if (_lossPrice.isGreaterThan(0) && _lossPrice.isLessThanOrEqualTo(lossPriceCompare!)) {
        throw UiUtils.showError(t('做空时止损触发价需大于{type}', { type: lossPriceType }))
      }
    }
    const params: OrderBuyParams = {
      symbolId: position.symbolId.toString(),
      qty: openInterest.value.divideStr(symbolInfo.value?.contractValue || 1),
      type: ContractOrderType.Both,
      positionType: position.direction,
      direction: position.direction === OrderDirection.Buy ? OrderDirection.Sell : OrderDirection.Buy,
      reduceOnly: ReduceOnly.Yes,
      profitTriggerPrice: profitPrice.value,
      lossTriggerPrice: lossPrice.value,
      profitTriggerType: isMarketPrice.value ? TriggerType.Mark : TriggerType.Latest,
      lossTriggerType: lossIsMarketPrice.value ? TriggerType.Mark : TriggerType.Latest,
    }
    let res: FetchResponse<any>
    if (submitLoading.value) return
    submitLoading.value = true
    if (allPosition.value) {
      // 全部仓位
      res = await ContractServices.doubleContractOpenPosition(params)
    } else {
      // 部分仓位
      res = await ContractServices.doubleContractsStopPartOrder(params)
    }
    if (res.status === ApiStatus.Success) {
      UiUtils.showSuccess(t('下单成功'))
      assetsStore.refresh()
      close()
    }
    submitLoading.value = false
  }

  return {
    stopProfitStopLossVisible,
    open,
    close,
    getSymbolIcon,
    markPrice,
    symbolInfo,
    lastPrice,
    switchAllPosition,
    allPosition,
    amount,
    totalAmount,
    sliderValue,
    sliderMarks,
    onSliderInput,
    switchMarketPrice,
    isMarketPrice,
    profitRateSliderMarks,
    sliderProfitRateValue,
    onProfitRateSliderInput,
    profitPrice,
    profit,
    profitRate,
    onProfitRateInput,
    onProfitPriceInput,
    switchLossIsMarketPrice,
    lossIsMarketPrice,
    sliderLossRateValue,
    lossRateSliderMarks,
    onLossRateSliderInput,
    lossPrice,
    loss,
    lossRate,
    onLossPriceInput,
    onLossRateInput,
    onAmountInput,
    onConfirmStopProfitStopLoss,
    submitLoading,
  }
}
